import { useEffect, useState, useRef } from 'react';
import {Image as ImageIcon, MapPin, Smile, X, Heart, Trash2, Send, Loader2, MessageCircleDashed as MessageCircle} from 'lucide-react';
import { lumi } from '@/lib/lumi';
import { useAuth } from '@/hooks/useAuth';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const MAX_CHARS = 280;

const EMOCOES = [
  { emoji: '😀', label: 'Feliz' },
  { emoji: '😍', label: 'Apaixonado' },
  { emoji: '😎', label: 'Tranquilo' },
  { emoji: '🥳', label: 'Comemorando' },
  { emoji: '😋', label: 'Com fome' },
  { emoji: '🔥', label: 'Animado' },
  { emoji: '😢', label: 'Triste' },
  { emoji: '😡', label: 'Irritado' },
  { emoji: '🍖', label: 'Churrasco' },
  { emoji: '🍷', label: 'Brindando' },
  { emoji: '💪', label: 'Determinado' },
  { emoji: '🙏', label: 'Grato' },
];

interface Post {
  _id: string;
  userId: string;
  userNome?: string;
  userFoto?: string;
  conteudo: string;
  imagens?: string[];
  localizacao?: string;
  emocao?: string;
  curtidas?: string[];
  createdAt: string;
}

interface Comentario {
  _id: string;
  postId: string;
  userId: string;
  userNome?: string;
  userFoto?: string;
  conteudo: string;
  createdAt: string;
}

function tempoRelativo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} d`;
  return new Date(iso).toLocaleDateString('pt-BR');
}

function Feed() {
  const { user, isAuthenticated } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [conteudo, setConteudo] = useState('');
  const [imagemUrl, setImagemUrl] = useState('');
  const [localizacao, setLocalizacao] = useState('');
  const [emocao, setEmocao] = useState('');
  const [showLocalInput, setShowLocalInput] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [perfilFoto, setPerfilFoto] = useState('');
  const [comentarios, setComentarios] = useState<Comentario[]>([]);
  const [postAberto, setPostAberto] = useState<string | null>(null);
  const [novoComentario, setNovoComentario] = useState<Record<string, string>>({});
  const [enviandoComentario, setEnviandoComentario] = useState<Record<string, boolean>>({});
  const [carregandoComentarios, setCarregandoComentarios] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const { list } = await lumi.entities.posts_feed.list({ sort: { createdAt: -1 }, limit: 100 });
      setPosts((list as Post[]) || []);
    } catch (error) {
      console.error('Falha ao carregar feed:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchComentarios = async () => {
    setCarregandoComentarios(true);
    try {
      const { list } = await lumi.entities.comentarios_feed.list({ sort: { createdAt: 1 }, limit: 500 });
      setComentarios((list as Comentario[]) || []);
    } catch (error) {
      console.error('Falha ao carregar comentarios:', error);
    } finally {
      setCarregandoComentarios(false);
    }
  };

  useEffect(() => {
    fetchComentarios();
  }, []);

  useEffect(() => {
    if (!user?.userId) return;
    lumi.entities.user_profiles.list({ filter: { userId: user.userId } }).
    then((res: any) => {
      const p = res?.list?.[0];
      if (p?.profileImageUrl) setPerfilFoto(p.profileImageUrl);
    }).
    catch(() => {});
  }, [user?.userId]);

  const handleUploadImagem = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const results = await lumi.tools.file.upload([file]);
      const url = results?.[0]?.fileUrl;
      if (url) setImagemUrl(url);
    } catch (error) {
      console.error('Falha no upload da imagem:', error);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handlePublicar = async () => {
    if (!isAuthenticated || !user?.userId) {
      window.location.href = '/cadastro';
      return;
    }
    if (!conteudo.trim() && !imagemUrl) return;
    setPublishing(true);
    try {
      const now = new Date().toISOString();
      await lumi.entities.posts_feed.create({
        userId: user.userId,
        userNome: user.userName || user.email || 'Usuario',
        userFoto: perfilFoto,
        conteudo: conteudo.trim().slice(0, MAX_CHARS),
        imagens: imagemUrl ? [imagemUrl] : [],
        localizacao: localizacao.trim(),
        emocao: emocao,
        curtidas: [],
        creator: user.userId,
        creatorEmail: user.email || '',
        createdAt: now,
        updatedAt: now,
      });
      setConteudo('');
      setImagemUrl('');
      setLocalizacao('');
      setEmocao('');
      setShowLocalInput(false);
      setShowEmojis(false);
      await fetchPosts();
    } catch (error) {
      console.error('Falha ao publicar:', error);
    } finally {
      setPublishing(false);
    }
  };

  const handleCurtir = async (post: Post) => {
    if (!user?.userId) return;
    const curtidas = post.curtidas || [];
    const jaCurtiu = curtidas.includes(user.userId);
    const novas = jaCurtiu ? curtidas.filter((id) => id !== user.userId) : [...curtidas, user.userId];
    setPosts((prev) => prev.map((p) => p._id === post._id ? { ...p, curtidas: novas } : p));
    try {
      await lumi.entities.posts_feed.update(post._id, { curtidas: novas, updatedAt: new Date().toISOString() });
    } catch (error) {
      console.error('Falha ao curtir:', error);
      await fetchPosts();
    }
  };

  const handleExcluir = async (post: Post) => {
    if (post.userId !== user?.userId) return;
    try {
      await lumi.entities.posts_feed.delete(post._id);
      setPosts((prev) => prev.filter((p) => p._id !== post._id));
    } catch (error) {
      console.error('Falha ao excluir post:', error);
    }
  };

  const handleToggleComentarios = (postId: string) => {
    setPostAberto((prev) => prev === postId ? null : postId);
  };

  const handleEnviarComentario = async (post: Post) => {
    if (!isAuthenticated || !user?.userId) {
      window.location.href = '/cadastro';
      return;
    }
    const texto = (novoComentario[post._id] || '').trim();
    if (!texto) return;
    setEnviandoComentario((prev) => ({ ...prev, [post._id]: true }));
    try {
      const now = new Date().toISOString();
      const criado = await lumi.entities.comentarios_feed.create({
        postId: post._id,
        userId: user.userId,
        userNome: user.userName || user.email || 'Usuario',
        userFoto: perfilFoto,
        conteudo: texto,
        creator: user.userId,
        creatorEmail: user.email || '',
        createdAt: now,
        updatedAt: now,
      });
      setComentarios((prev) => [...prev, criado as Comentario]);
      setNovoComentario((prev) => ({ ...prev, [post._id]: '' }));
    } catch (error) {
      console.error('Falha ao comentar:', error);
    } finally {
      setEnviandoComentario((prev) => ({ ...prev, [post._id]: false }));
    }
  };

  const handleExcluirComentario = async (comentario: Comentario) => {
    if (comentario.userId !== user?.userId) return;
    try {
      await lumi.entities.comentarios_feed.delete(comentario._id);
      setComentarios((prev) => prev.filter((c) => c._id !== comentario._id));
    } catch (error) {
      console.error('Falha ao excluir comentario:', error);
    }
  };

  const restante = MAX_CHARS - conteudo.length;
  const inicialAutor = (user?.userName || 'U').charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <Navbar />

      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-black text-white mb-1">Feed</h1>
        <p className="text-gray-400 text-sm mb-6">Veja o que a comunidade esta compartilhando.</p>

        {/* Composer */}
        <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] p-4 mb-8 shadow-lg">
          <div className="flex gap-3">
            {perfilFoto ?
            <img src={perfilFoto} alt="Voce" className="w-11 h-11 rounded-full object-cover border-2 border-[#8D0000] flex-shrink-0" /> :
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center text-white font-bold flex-shrink-0">
                {inicialAutor}
              </div>
            }
            <div className="flex-1">
              <textarea
                value={conteudo}
                onChange={(e) => setConteudo(e.target.value.slice(0, MAX_CHARS))}
                placeholder={isAuthenticated ? 'No que voce esta pensando?' : 'Entre para publicar no feed...'}
                rows={3}
                className="w-full bg-transparent text-white placeholder-gray-500 resize-none outline-none text-base" />

              {emocao &&
              <div className="inline-flex items-center gap-1 mt-1 text-sm text-[#E57335] bg-[#E57335]/10 px-2 py-1 rounded-full">
                  <span>{emocao}</span>
                  <button onClick={() => setEmocao('')} className="text-gray-400 hover:text-white">
                    <X size={12} />
                  </button>
                </div>
              }

              {imagemUrl &&
              <div className="relative mt-3 rounded-xl overflow-hidden border border-[#3a3a3a]">
                  <img src={imagemUrl} alt="Previa" className="w-full max-h-80 object-cover" />
                  <button
                  onClick={() => setImagemUrl('')}
                  className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full w-8 h-8 flex items-center justify-center">
                    <X size={16} />
                  </button>
                </div>
              }

              {showLocalInput &&
              <div className="flex items-center gap-2 mt-3 bg-[#1f1f1f] rounded-lg px-3 py-2">
                  <MapPin size={16} className="text-[#E53935]" />
                  <input
                  type="text"
                  value={localizacao}
                  onChange={(e) => setLocalizacao(e.target.value)}
                  placeholder="Adicionar localizacao (ex: Milao, Italia)"
                  className="flex-1 bg-transparent text-white placeholder-gray-500 outline-none text-sm" />
                </div>
              }

              {showEmojis &&
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mt-3 bg-[#1f1f1f] rounded-lg p-3">
                  {EMOCOES.map((em) =>
                <button
                  key={em.label}
                  onClick={() => { setEmocao(`${em.emoji} ${em.label}`); setShowEmojis(false); }}
                  title={em.label}
                  className="text-2xl hover:scale-125 transition-transform">
                      {em.emoji}
                    </button>
                )}
                </div>
              }

              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#3a3a3a]">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    title="Adicionar imagem"
                    className="w-9 h-9 flex items-center justify-center rounded-full text-[#E57335] hover:bg-[#E57335]/10 transition-colors">
                    {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImageIcon size={18} />}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleUploadImagem} className="hidden" />
                  <button
                    onClick={() => setShowLocalInput(!showLocalInput)}
                    title="Adicionar localizacao"
                    className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors ${showLocalInput || localizacao ? 'bg-[#E53935]/10 text-[#E53935]' : 'text-[#E53935] hover:bg-[#E53935]/10'}`}>
                    <MapPin size={18} />
                  </button>
                  <button
                    onClick={() => setShowEmojis(!showEmojis)}
                    title="Adicionar emocao"
                    className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors ${showEmojis || emocao ? 'bg-[#E57335]/10 text-[#E57335]' : 'text-[#E57335] hover:bg-[#E57335]/10'}`}>
                    <Smile size={18} />
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium ${restante < 0 ? 'text-red-500' : restante < 30 ? 'text-[#E57335]' : 'text-gray-500'}`}>
                    {restante}
                  </span>
                  <button
                    onClick={handlePublicar}
                    disabled={publishing || (!conteudo.trim() && !imagemUrl)}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-5 py-2 rounded-full font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-[#E53935]/40 transition-all">
                    {publishing ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    Publicar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de posts */}
        {loading ?
        <div className="flex justify-center py-12">
            <Loader2 size={32} className="animate-spin text-[#E53935]" />
          </div> :
        posts.length === 0 ?
        <div className="text-center py-16 text-gray-500">
            <Smile size={48} className="mx-auto mb-3 opacity-40" />
            <p>Nenhuma publicacao ainda. Seja o primeiro a postar!</p>
          </div> :

        <div className="space-y-5">
            {posts.map((post) => {
            const curtido = !!user?.userId && (post.curtidas || []).includes(user.userId);
            const inicial = (post.userNome || 'U').charAt(0).toUpperCase();
            const comentariosDoPost = comentarios.filter((c) => c.postId === post._id);
            const painelAberto = postAberto === post._id;
            return (
              <div key={post._id} className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] p-4 shadow-md">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {post.userFoto ?
                    <img src={post.userFoto} alt={post.userNome} className="w-11 h-11 rounded-full object-cover border-2 border-[#8D0000]" /> :
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center text-white font-bold">
                          {inicial}
                        </div>
                    }
                      <div>
                        <p className="text-white font-semibold leading-tight">{post.userNome || 'Usuario'}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>{tempoRelativo(post.createdAt)}</span>
                          {post.localizacao &&
                        <span className="flex items-center gap-1">
                              <MapPin size={11} className="text-[#E53935]" /> {post.localizacao}
                            </span>
                        }
                        </div>
                      </div>
                    </div>
                    {post.userId === user?.userId &&
                  <button
                    onClick={() => handleExcluir(post)}
                    title="Excluir post"
                    className="text-gray-500 hover:text-red-500 transition-colors p-1">
                        <Trash2 size={16} />
                      </button>
                  }
                  </div>

                  {post.emocao &&
                <div className="mt-2 inline-flex items-center gap-1 text-sm text-[#E57335] bg-[#E57335]/10 px-2.5 py-0.5 rounded-full">
                      {post.emocao}
                    </div>
                }

                  {post.conteudo &&
                <p className="text-gray-200 mt-3 whitespace-pre-wrap leading-relaxed">{post.conteudo}</p>
                }

                  {post.imagens && post.imagens.length > 0 &&
                <div className="mt-3 rounded-xl overflow-hidden border border-[#3a3a3a]">
                      <img src={post.imagens[0]} alt="Post" className="w-full max-h-[480px] object-cover" />
                    </div>
                }

                  <div className="flex items-center gap-4 mt-4 pt-3 border-t border-[#3a3a3a]">
                    <button
                    onClick={() => handleCurtir(post)}
                    className={`flex items-center gap-2 text-sm font-medium transition-colors ${curtido ? 'text-[#E53935]' : 'text-gray-400 hover:text-[#E53935]'}`}>
                      <Heart size={18} className={curtido ? 'fill-[#E53935]' : ''} />
                      {(post.curtidas || []).length > 0 && (post.curtidas || []).length}
                      <span>Curtir</span>
                    </button>
                    <button
                    onClick={() => handleToggleComentarios(post._id)}
                    className={`flex items-center gap-2 text-sm font-medium transition-colors ${painelAberto ? 'text-[#E57335]' : 'text-gray-400 hover:text-[#E57335]'}`}>
                      <MessageCircle size={18} className={painelAberto ? 'fill-[#E57335]/20' : ''} />
                      {comentariosDoPost.length > 0 && comentariosDoPost.length}
                      <span>Comentar</span>
                    </button>
                  </div>

                  {painelAberto &&
                <div className="mt-3 pt-3 border-t border-[#3a3a3a] space-y-3">
                      {carregandoComentarios && comentariosDoPost.length === 0 &&
                  <div className="flex justify-center py-3">
                          <Loader2 size={20} className="animate-spin text-[#E53935]" />
                        </div>
                  }

                      {!carregandoComentarios && comentariosDoPost.length === 0 &&
                  <p className="text-sm text-gray-500 text-center py-2">Nenhum comentario ainda. Seja o primeiro!</p>
                  }

                      {comentariosDoPost.map((comentario) => {
                    const inicialComentario = (comentario.userNome || 'U').charAt(0).toUpperCase();
                    return (
                      <div key={comentario._id} className="flex items-start gap-2.5">
                            {comentario.userFoto ?
                        <img src={comentario.userFoto} alt={comentario.userNome} className="w-8 h-8 rounded-full object-cover border border-[#8D0000] flex-shrink-0" /> :

                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                {inicialComentario}
                              </div>
                        }
                            <div className="flex-1 bg-[#1f1f1f] rounded-xl px-3 py-2">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-white text-sm font-semibold leading-tight">{comentario.userNome || 'Usuario'}</p>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500">{tempoRelativo(comentario.createdAt)}</span>
                                  {comentario.userId === user?.userId &&
                            <button
                              onClick={() => handleExcluirComentario(comentario)}
                              title="Excluir comentario"
                              className="text-gray-500 hover:text-red-500 transition-colors">
                                      <Trash2 size={13} />
                                    </button>
                            }
                                </div>
                              </div>
                              <p className="text-gray-200 text-sm mt-0.5 whitespace-pre-wrap leading-relaxed">{comentario.conteudo}</p>
                            </div>
                          </div>);

                  })}

                      <div className="flex items-center gap-2 pt-1">
                        {perfilFoto ?
                    <img src={perfilFoto} alt="Voce" className="w-8 h-8 rounded-full object-cover border border-[#8D0000] flex-shrink-0" /> :

                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {inicialAutor}
                          </div>
                    }
                        <input
                      type="text"
                      value={novoComentario[post._id] || ''}
                      onChange={(e) => setNovoComentario((prev) => ({ ...prev, [post._id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleEnviarComentario(post); }}
                      placeholder={isAuthenticated ? 'Escreva um comentario...' : 'Entre para comentar...'}
                      className="flex-1 bg-[#1f1f1f] text-white placeholder-gray-500 text-sm rounded-full px-4 py-2 outline-none border border-transparent focus:border-[#E57335]" />

                        <button
                      onClick={() => handleEnviarComentario(post)}
                      disabled={!!enviandoComentario[post._id] || !(novoComentario[post._id] || '').trim()}
                      className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-[#E57335] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 transition-all">
                          {enviandoComentario[post._id] ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                        </button>
                      </div>
                    </div>
              }
                </div>);

          })}
          </div>
        }
      </div>

      <Footer />
    </div>);

}

export default Feed;
